/**
 * API client for backend communication
 */

import { startAuthentication } from '@simplewebauthn/browser';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/types';

import { getApiBase } from './apiBase';

const API_BASE = getApiBase();

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?:
    | {
        code: string;
        message: string;
        details?: Record<string, unknown>;
      }
    | string;
  requestId?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Check if this is a network connectivity error
   */
  get isNetworkError(): boolean {
    return this.code === 'NETWORK_ERROR';
  }
}

let stepUpInFlight: Promise<void> | null = null;

const normalizeApiError = (
  error: ApiResponse<unknown>['error']
): { code?: string; message?: string; details?: Record<string, unknown> } => {
  if (!error) return {};
  if (typeof error === 'string') {
    return { code: 'UNKNOWN_ERROR', message: error };
  }
  return error;
};

async function performStepUp(): Promise<void> {
  if (stepUpInFlight) return stepUpInFlight;

  stepUpInFlight = (async () => {
    const startResponse = await fetch(`${API_BASE}/api/auth/step-up/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    });

    const startData: ApiResponse<{
      challengeId: string;
      options: PublicKeyCredentialRequestOptionsJSON;
    }> = await startResponse.json();

    if (!startResponse.ok || !startData.success || !startData.data) {
      const normalizedError = normalizeApiError(startData.error);
      throw new ApiError(
        normalizedError.code || 'STEP_UP_START_FAILED',
        normalizedError.message || 'Error al iniciar step-up con passkey',
        startResponse.status,
        normalizedError.details
      );
    }

    const { challengeId, options } = startData.data;

    let credential: AuthenticationResponseJSON;
    try {
      credential = await startAuthentication({ optionsJSON: options });
    } catch (err) {
      if (err instanceof Error && err.name === 'NotAllowedError') {
        throw new ApiError('STEP_UP_CANCELLED', 'Autenticación cancelada', 0);
      }
      throw new ApiError(
        'STEP_UP_WEBAUTHN_ERROR',
        err instanceof Error ? err.message : 'Error de autenticación',
        0
      );
    }

    const finishResponse = await fetch(`${API_BASE}/api/auth/step-up/finish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Challenge-ID': challengeId,
      },
      credentials: 'include',
      body: JSON.stringify({ response: credential }),
    });

    const finishData: ApiResponse<{ message: string }> = await finishResponse.json();

    if (!finishResponse.ok || !finishData.success) {
      const normalizedError = normalizeApiError(finishData.error);
      throw new ApiError(
        normalizedError.code || 'STEP_UP_FINISH_FAILED',
        normalizedError.message || 'Error al completar step-up con passkey',
        finishResponse.status
      );
    }
  })();

  try {
    await stepUpInFlight;
  } finally {
    stepUpInFlight = null;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  allowStepUpRetry: boolean = true
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });
  } catch (error) {
    // Network error - fetch failed completely
    throw new ApiError(
      'NETWORK_ERROR',
      'Conexion perdida. Verifique su conexion a internet.',
      0
    );
  }

  let data: ApiResponse<T>;
  try {
    data = await response.json();
  } catch {
    // Response is not valid JSON
    throw new ApiError(
      'PARSE_ERROR',
      'Error al procesar la respuesta del servidor',
      response.status
    );
  }

  if (!response.ok || !data.success) {
    const message =
      typeof data.error === 'string'
        ? data.error
        : data.error?.message;
    const code =
      typeof data.error === 'string'
        ? 'UNKNOWN_ERROR'
        : data.error?.code;
    const details =
      typeof data.error === 'string'
        ? undefined
        : data.error?.details;

    const error = new ApiError(
      code || 'UNKNOWN_ERROR',
      message || 'Error desconocido',
      response.status,
      details
    );

    if (allowStepUpRetry && error.code === 'STEP_UP_REQUIRED') {
      await performStepUp();
      return request<T>(endpoint, options, false);
    }

    throw error;
  }

  return data.data as T;
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),

  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown, options?: { headers?: Record<string, string> }) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      headers: options?.headers,
    }),

  delete: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }),
};

// Auth API
export const authApi = {
  registerStart: (email: string) =>
    api.post<{ challengeId: string; options: unknown }>('/api/auth/register/start', { email }),

  registerFinish: (email: string, response: unknown, challengeId: string) =>
    request<{ message: string }>('/api/auth/register/finish', {
      method: 'POST',
      headers: { 'X-Challenge-ID': challengeId },
      body: JSON.stringify({ email, response }),
    }),

  loginStart: (email?: string) =>
    api.post<{ challengeId: string; options: unknown }>('/api/auth/login/start', { email }),

  loginFinish: (response: unknown, challengeId: string) =>
    request<{ user: { id: string; email: string; name: string; role: string } }>(
      '/api/auth/login/finish',
      {
        method: 'POST',
        headers: { 'X-Challenge-ID': challengeId },
        body: JSON.stringify({ response }),
      }
    ),

  logout: () => api.post<{ message: string }>('/api/auth/logout'),

  me: () =>
    api.get<{ id: string; email: string; name: string; role: string }>('/api/auth/me'),
};

// Portal Auth API
export const portalAuthApi = {
  requestAccess: (email: string) =>
    api.post<{ message: string }>('/api/portal/auth/request', { email }),

  verify: (token: string) =>
    api.post<{ customer: { id: string; email: string; name: string } }>(
      '/api/portal/auth/verify',
      { token }
    ),

  me: () =>
    api.get<{ id: string; email: string; name: string; phone?: string }>('/api/portal/auth/me'),

  logout: () => api.post<{ message: string }>('/api/portal/auth/logout'),
};
